import { FC, MouseEvent } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { renderLabel, getItemUrl, checkIs3DImage, checkIsVideo } from './helpers'
import { Image } from '../image/Image'
import { Typography } from '@mui/material'
import type { ContentItem } from '@obeta/data/lib/hooks/useArticleGalleryContent'
import { ReactComponent as MoreImagePlaceholder } from 'assets/icon/designsystem/image.svg'
import styles from './InlineMediaBox.module.scss'

type GalleryStripSectionProps = {
  items: ContentItem[]
  itemsLeft: number
  isShowMorePlaceholderVisible: boolean
  onSelectItemByIndex: (index: number) => void
  onMouseEnter: (index: number, e: MouseEvent<HTMLDivElement>) => void
  activeMagnifiedIndex?: number
}

export const GalleryStripSection: FC<GalleryStripSectionProps> = ({
  items,
  itemsLeft,
  isShowMorePlaceholderVisible,
  onSelectItemByIndex,
  onMouseEnter,
  activeMagnifiedIndex,
}) => {
  const { t } = useTranslation()

  return (
    <div className={styles.galleryStripSection}>
      {items.map((item, index) => {
        const contentTypeLabel = renderLabel(item, t)
        const noZoom = checkIs3DImage(item) || checkIsVideo(item)

        return (
          <div
            key={`stripe-${index}`}
            className={clsx(styles.mainImageWrapper, styles.stripImageWrapper, {
              [styles.activeMagnified]: activeMagnifiedIndex === item.itemIndex,
              [styles.noZoom]: noZoom,
            })}
            role="button"
            tabIndex={0}
            onMouseMove={noZoom ? undefined : (e) => onMouseEnter(item.itemIndex, e)}
            onClick={() => onSelectItemByIndex(item.itemIndex)}
          >
            <Image
              variant="product"
              src={getItemUrl(item)}
              alt={item.description}
              className={clsx(styles.galleryStripImage, contentTypeLabel && styles.transparent)}
            />
            {contentTypeLabel}
          </div>
        )
      })}

      {isShowMorePlaceholderVisible && (
        <div
          key="showMoreLabel"
          className={clsx(
            styles.mainImageWrapper,
            styles.stripImageWrapper,
            styles.moreImageCountLabel
          )}
          role="button"
          tabIndex={0}
          onClick={() => onSelectItemByIndex(0)}
        >
          <div className={styles.moreImageCountLabelText}>
            <Typography className={styles.countMessage} variant="bodyBold">
              +{itemsLeft}
            </Typography>
          </div>
          <MoreImagePlaceholder className={styles.moreImagePlaceholderSvg} />
        </div>
      )}
    </div>
  )
}
